import {
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import {
  QuizToastActions,
  apiToaster,
} from '~teacher/components/quizzes/toastActions/';

import { QuizItemCache } from '../../api/quiz/getQuizzes/normalize/toQuizItemCache';
import { uniqueIdFactory } from '../../api/quiz/getQuizzes/normalize/uniqueIds';
import { Quiz } from '../../api/quiz/getQuizzes/types';
import {
  UpdateQuizParameters,
  UpdateQuizResponse,
  updateQuiz,
} from '../../api/quizApi';
import { quizzesQueryKey, updateQuizItemCacheById } from '.';

export type Context = {
  prevQuizById: Quiz;
};
export type UseUpdateQuizOptions = UseMutationOptions<
  UpdateQuizResponse,
  unknown,
  UpdateQuizParameters,
  Context
>;

export function useUpdateQuiz() {
  const queryClient = useQueryClient();

  return useMutation(updateQuiz, {
    onMutate: ({ quizId, isSharable }: UpdateQuizParameters) => {
      queryClient.cancelQueries(quizzesQueryKey.quiz);

      const prevQuizCache = queryClient.getQueryData<QuizItemCache>(
        quizzesQueryKey.quiz
      );

      const item =
        prevQuizCache.itemById[uniqueIdFactory.quiz.toUniqueId(quizId)];

      if (item?.type === 'quiz') {
        updateQuizItemCacheById(queryClient, {
          quizId: quizId,
          updatedQuiz: {
            ...item,
            sharable: isSharable,
          },
        });

        return { prevQuizById: item };
      }
      return { prevQuizById: null };
    },

    onError: (
      error: unknown,
      { quizId }: UpdateQuizParameters,
      context: Context
    ) => {
      const { prevQuizById } = context;

      apiToaster([], QuizToastActions.ERROR, error);

      // Rollback with the previous single updated quiz
      updateQuizItemCacheById(queryClient, {
        quizId: quizId,
        updatedQuiz: { ...prevQuizById },
      });
    },
  });
}
